import { useState } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, FormControlLabel, Grid, IconButton, Stack, Switch, Tab, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import SectionHeader from '../../../components/shared/SectionHeader';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import EditableField from '../../../components/EditableField';
import { INCOME_TYPES, EMPLOYMENT_CLASSIFICATION_TYPES, OWNERSHIP_SHARE_TYPES, US_STATES } from '../../../services/mock/urlaEnumerations';

/**
 * URLA Section 1b — Employment Information
 * URLA Section 1c — Other Income and Income from Other Sources
 * Covers: current + previous employment history, self-employment indicator,
 * ownership interest, employer details, all MISMO 3.4 income types.
 */

const formatCurrency = (val) => {
  if (val == null) return '—';
  return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
};

import DataItem from '../../../components/shared/DataItem';

const EMPTY_EMPLOYMENT = {
  incomeId: null,
  employerName: '',
  employerPhone: '',
  employerAddress: { street: '', city: '', state: '', zip: '' },
  position: '',
  startDate: '',
  endDate: '',
  isCurrent: true,
  classification: '',
  isSelfEmployed: false,
  ownershipShare: '',
  monthlyIncome: 0,
  yearsInProfession: null,
};

const EmploymentCard = ({ job, jobIndex, namePath, isEditMode, onRemove }) => {
  const { setValue } = useFormContext();

  const durationText = () => {
    const start = job.startDate ? new Date(job.startDate) : null;
    const end = job.endDate ? new Date(job.endDate) : new Date();
    if (!start) return '';
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return years > 0 ? `${years} yr ${rem} mo` : `${rem} mo`;
  };

  const sp = isEditMode ? 2 : 1;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <WorkIcon fontSize="small" color="secondary" />
            <Typography variant="subtitle2">{job.employerName || `Employment #${jobIndex + 1}`}</Typography>
            {job.isCurrent && <Chip label="Current" size="small" color="success" variant="outlined" />}
            {job.isSelfEmployed && <Chip label="Self-Employed" size="small" color="secondary" variant="outlined" />}
          </Stack>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">{durationText()}</Typography>
            {isEditMode && onRemove && <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onRemove}>Remove</Button>}
          </Stack>
        }
      />
      <CardContent>
        <Grid container spacing={sp}>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <EditableField label="Employer Name" value={job.employerName} name={`${namePath}.employerName`} isEditMode={isEditMode} required={isEditMode} placeholder="Company Name" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <EditableField label="Position / Title" value={job.position} name={`${namePath}.position`} isEditMode={isEditMode} required={isEditMode} placeholder="Job Title" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <EditableField label="Start Date" value={job.startDate} name={`${namePath}.startDate`} isEditMode={isEditMode} type="date" required={isEditMode} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            {isEditMode ? (
              <FormControlLabel control={<Switch checked={job.isCurrent} onChange={() => setValue(`${namePath}.isCurrent`, !job.isCurrent, { shouldDirty: true })} size="small" />} label="Currently employed here" sx={{ mt: 3 }} />
            ) : (
              <DataItem label="End Date">{job.isCurrent ? 'Present' : formatDate(job.endDate)}</DataItem>
            )}
          </Grid>
          {isEditMode && !job.isCurrent && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <EditableField label="End Date" value={job.endDate} name={`${namePath}.endDate`} isEditMode type="date" />
            </Grid>
          )}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <EditableField label="Classification" value={isEditMode ? job.classification : (EMPLOYMENT_CLASSIFICATION_TYPES.find(c => c.value === job.classification)?.label || job.classification)} name={`${namePath}.classification`} isEditMode={isEditMode} options={isEditMode ? EMPLOYMENT_CLASSIFICATION_TYPES : null} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <EditableField label="Monthly Income (Base)" value={job.monthlyIncome} name={`${namePath}.monthlyIncome`} isEditMode={isEditMode} type="currency" required={isEditMode} placeholder="12,500" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <EditableField label="Years in Profession" value={job.yearsInProfession} name={`${namePath}.yearsInProfession`} isEditMode={isEditMode} type={isEditMode ? 'number' : 'text'} placeholder="10" />
          </Grid>
          {isEditMode && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <FormControlLabel control={<Switch checked={job.isSelfEmployed} onChange={() => setValue(`${namePath}.isSelfEmployed`, !job.isSelfEmployed, { shouldDirty: true })} size="small" />} label="Self-Employed" sx={{ mt: 3 }} />
            </Grid>
          )}
          {job.isSelfEmployed && job.ownershipShare && (
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <EditableField label="Ownership Share" value={isEditMode ? job.ownershipShare : (OWNERSHIP_SHARE_TYPES.find(o => o.value === job.ownershipShare)?.label || job.ownershipShare)} name={`${namePath}.ownershipShare`} isEditMode={isEditMode} options={isEditMode ? OWNERSHIP_SHARE_TYPES : null} />
            </Grid>
          )}
        </Grid>

        <Typography variant="overline" color="text.secondary" sx={{ mt: 2, mb: 1, display: 'block' }}>Employer Contact</Typography>
        <Grid container spacing={sp}>
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <EditableField label="Employer Phone" value={job.employerPhone} name={`${namePath}.employerPhone`} isEditMode={isEditMode} type="tel" placeholder="(555) 800-1234" />
          </Grid>
          {isEditMode ? (
            <>
              <Grid size={{ xs: 12, sm: 8 }}>
                <EditableField label="Employer Street" value={job.employerAddress?.street} name={`${namePath}.employerAddress.street`} isEditMode placeholder="100 Main St" />
              </Grid>
              <Grid size={4}>
                <EditableField label="City" value={job.employerAddress?.city} name={`${namePath}.employerAddress.city`} isEditMode placeholder="Los Angeles" />
              </Grid>
              <Grid size={4}>
                <EditableField label="State" value={job.employerAddress?.state} name={`${namePath}.employerAddress.state`} isEditMode options={US_STATES} />
              </Grid>
              <Grid size={4}>
                <EditableField label="ZIP" value={job.employerAddress?.zip} name={`${namePath}.employerAddress.zip`} isEditMode placeholder="90001" />
              </Grid>
            </>
          ) : (
            job.employerAddress && (
              <Grid size={{ xs: 12, sm: 8, md: 5 }}>
                <DataItem label="Employer Address">{[job.employerAddress.street, `${job.employerAddress.city}, ${job.employerAddress.state} ${job.employerAddress.zip}`].join(', ')}</DataItem>
              </Grid>
            )
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const OtherIncomeTable = ({ items, namePath, isEditMode, onAdd, onRemove }) => {
  const getLabel = (type) => INCOME_TYPES.find(i => i.value === type)?.label || type;

  const totalMonthly = items.reduce((sum, i) => sum + (i.monthlyAmount || 0), 0);

  if (!isEditMode) {
    if (!items || items.length === 0) {
      return <Typography variant="body2" color="text.secondary">No additional income reported.</Typography>;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Income Type</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Monthly Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.incomeId}>
              <TableCell>{getLabel(item.incomeType)}</TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">{item.description || '—'}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color="success.main">{formatCurrency(item.monthlyAmount)}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}><Typography fontWeight={700}>Total Other Income</Typography></TableCell>
            <TableCell align="right"><Typography fontWeight={700} color="success.main">{formatCurrency(totalMonthly)}</Typography></TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    );
  }

  return (
    <Box>
      {items.map((item, idx) => (
        <Grid container spacing={2} key={item.incomeId || idx} sx={{ mb: 1 }} alignItems="flex-end">
          <Grid size={4}><EditableField label="Income Type" value={item.incomeType} name={`${namePath}.${idx}.incomeType`} isEditMode options={INCOME_TYPES} /></Grid>
          <Grid size={3}><EditableField label="Monthly Amount" value={item.monthlyAmount} name={`${namePath}.${idx}.monthlyAmount`} isEditMode type="currency" placeholder="500" /></Grid>
          <Grid size={4}><EditableField label="Description" value={item.description} name={`${namePath}.${idx}.description`} isEditMode placeholder="Source details" /></Grid>
          <Grid size={1}><IconButton size="small" color="error" onClick={() => onRemove(idx)} title="Remove" sx={{ mb: 2 }}><DeleteIcon fontSize="small" /></IconButton></Grid>
        </Grid>
      ))}
      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>Add Income Source</Button>
    </Box>
  );
};

const BorrowerIncomeSection = ({ borrower, borrowerIndex, namePath, label, isEditMode }) => {
  if (!borrower) return null;

  const { control, setValue, getValues } = useFormContext();
  const watchedEmployment = useWatch({ control, name: `${namePath}.employment` });
  const watchedOtherIncome = useWatch({ control, name: `${namePath}.otherIncome` });

  const employment = isEditMode ? (watchedEmployment || []) : (borrower.employment || []);
  const otherIncome = isEditMode ? (watchedOtherIncome || []) : (borrower.otherIncome || []);

  const currentJobs = employment.filter(j => j.isCurrent);
  const previousJobs = employment.filter(j => !j.isCurrent);

  const totalEmploymentIncome = currentJobs.reduce((s, j) => s + (j.monthlyIncome || 0), 0);
  const totalOtherIncome = otherIncome.reduce((s, i) => s + (i.monthlyAmount || 0), 0);
  const totalMonthlyIncome = totalEmploymentIncome + totalOtherIncome;

  const handleAddJob = (isCurrent = true) => {
    const newJob = { ...JSON.parse(JSON.stringify(EMPTY_EMPLOYMENT)), incomeId: `new-${Date.now()}`, isCurrent };
    const currentEmp = getValues(`${namePath}.employment`) || [];
    setValue(`${namePath}.employment`, [...currentEmp, newJob], { shouldDirty: true });
  };

  const handleRemoveJob = (jobToRemove) => {
    const currentEmp = getValues(`${namePath}.employment`) || [];
    const updated = currentEmp.filter(j => (j.incomeId || j.id) !== (jobToRemove.incomeId || jobToRemove.id));
    setValue(`${namePath}.employment`, updated, { shouldDirty: true });
  };

  const handleAddOtherIncome = () => {
    const newId = `new-${Date.now()}`;
    const current = getValues(`${namePath}.otherIncome`) || [];
    setValue(`${namePath}.otherIncome`, [...current, { incomeId: newId, incomeType: '', monthlyAmount: 0, description: '' }], { shouldDirty: true });
  };

  const handleRemoveOtherIncome = (idx) => {
    const current = getValues(`${namePath}.otherIncome`) || [];
    setValue(`${namePath}.otherIncome`, current.filter((_, i) => i !== idx), { shouldDirty: true });
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{label}</Typography>

      {/* Current Employment — Section 1b */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Current Employment</Typography>
        {isEditMode && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddJob(true)}>Add Current Employer</Button>
        )}
      </Stack>
      {currentJobs.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No current employment on record.
          {isEditMode && <span> Click <strong>Add Current Employer</strong> above to add one.</span>}
        </Alert>
      ) : (
        employment.map((job, idx) => job.isCurrent ? (
          <EmploymentCard
            key={job.incomeId || idx}
            job={job}
            jobIndex={idx}
            namePath={`${namePath}.employment.${idx}`}
            isEditMode={isEditMode}
            onRemove={isEditMode ? () => handleRemoveJob(job) : undefined}
          />
        ) : null)
      )}

      {/* Previous Employment */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, mt: 3 }}>
        <Typography variant="subtitle2">Previous Employment</Typography>
        {isEditMode && (
          <Button size="small" variant="outlined" color="inherit" startIcon={<AddIcon />} onClick={() => handleAddJob(false)}>Add Previous Employer</Button>
        )}
      </Stack>
      {previousJobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No previous employment on record.</Typography>
      ) : (
        employment.map((job, idx) => !job.isCurrent ? (
          <EmploymentCard
            key={job.incomeId || idx}
            job={job}
            jobIndex={idx}
            namePath={`${namePath}.employment.${idx}`}
            isEditMode={isEditMode}
            onRemove={isEditMode ? () => handleRemoveJob(job) : undefined}
          />
        ) : null)
      )}

      {/* Other Income — Section 1c */}
      <Card variant="outlined" sx={{ mt: 3, mb: 2 }}>
        <CardHeader
          title={<SectionHeader title="Other Income / Income from Other Sources" icon={<AttachMoneyIcon fontSize="small" />} sx={{ mb: 0 }} />}
          subheader="(URLA Section 1c)"
          subheaderTypographyProps={{ variant: 'caption' }}
        />
        <CardContent>
          <OtherIncomeTable
            items={otherIncome}
            namePath={`${namePath}.otherIncome`}
            isEditMode={isEditMode}
            onAdd={handleAddOtherIncome}
            onRemove={handleRemoveOtherIncome}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

const IncomeEmploymentTab = ({ loanDetail, isEditMode }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!loanDetail) return null;

  const borrowers = loanDetail.borrowers || [];

  // Income helper (for sidebar — uses loanDetail directly)
  const getIncome = (b) => {
    const emp = (b?.employment || []).filter(j => j.isCurrent).reduce((s, j) => s + (j.monthlyIncome || 0), 0);
    const other = (b?.otherIncome || []).reduce((s, i) => s + (i.monthlyAmount || 0), 0);
    return emp + other;
  };

  // Build borrower list with labels
  const allBorrowers = borrowers.map((b, i) => ({
    borrower: b,
    label: i === 0
      ? 'Primary Borrower'
      : (b.borrowerType === 'Spouse Co-Borrower'
        ? `Spouse${b.firstName ? ` — ${b.firstName} ${b.lastName || ''}`.trim() : ''}`
        : `Co-Borrower${b.firstName ? ` — ${b.firstName} ${b.lastName || ''}`.trim() : ''}`),
    name: `${b.firstName || ''} ${b.lastName || ''}`.trim(),
    isPrimary: i === 0,
    index: i,
  }));

  const grandTotal = allBorrowers.reduce((sum, b) => sum + getIncome(b.borrower), 0);
  const safeTab = Math.min(activeTab, allBorrowers.length - 1);
  const current = allBorrowers[safeTab];

  const showSidebar = !isEditMode;

  // Per-borrower income breakdown helper
  const getBorrowerBreakdown = (b) => {
    const emp = b?.employment || [];
    const currentJobs = emp.filter(j => j.isCurrent);
    const employmentIncome = currentJobs.reduce((s, j) => s + (j.monthlyIncome || 0), 0);
    const otherIncome = (b?.otherIncome || []).reduce((s, i) => s + (i.monthlyAmount || 0), 0);
    return { currentJobs, employmentIncome, otherIncome, total: employmentIncome + otherIncome };
  };

  // Income summary sidebar
  const incomeSidebar = showSidebar ? (
    <Box sx={{ position: 'sticky', top: 16 }}>
      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <SectionHeader title="Income Summary" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />

          {/* Grand total */}
          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">Household Monthly Income</Typography>
            <Typography variant="h6" fontWeight={700} color="primary">{formatCurrency(grandTotal)}<Typography component="span" variant="caption" color="text.secondary">/mo</Typography></Typography>
          </Box>

          <Stack spacing={1.5}>
            {allBorrowers.map((b, i) => {
              const bd = getBorrowerBreakdown(b.borrower);
              return (
                <Box key={i}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon sx={{ fontSize: 16, color: 'action.active' }} />
                      <Typography variant="subtitle2" fontWeight={700}>{b.name || b.label}</Typography>
                    </Stack>
                    <Typography variant="subtitle2" fontWeight={700} color="primary">{formatCurrency(bd.total)}</Typography>
                  </Stack>

                  <Box sx={{ pl: 0.5 }}>
                    {bd.currentJobs.map((job, ji) => (
                      <Box key={ji} sx={{ mb: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{job.employerName || 'Employer'}</Typography>
                          <Typography variant="caption" fontWeight={600}>{formatCurrency(job.monthlyIncome)}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.disabled">{job.position}{job.startDate ? ` · since ${new Date(job.startDate).getFullYear()}` : ''}</Typography>
                      </Box>
                    ))}
                    {bd.currentJobs.length === 0 && (
                      <Typography variant="caption" color="text.secondary" fontStyle="italic">No current employment</Typography>
                    )}
                    {bd.otherIncome > 0 && (
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">Other Income</Typography>
                        <Typography variant="caption" fontWeight={600}>{formatCurrency(bd.otherIncome)}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" fontWeight={600}>Subtotal</Typography>
                      <Typography variant="caption" fontWeight={700}>{formatCurrency(bd.total)}/mo</Typography>
                    </Stack>
                  </Box>

                  {i < allBorrowers.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }} />}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  ) : null;

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left column: Income detail sections */}
        <Grid size={{ xs: 12, lg: showSidebar ? 8 : 12 }}>

          {/* Borrower Tabs */}
          {allBorrowers.length > 1 && (
            <Tabs value={safeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              {allBorrowers.map((b, i) => (
                <Tab
                  key={i}
                  icon={i === 0 ? <PersonIcon fontSize="small" /> : <GroupIcon fontSize="small" />}
                  iconPosition="start"
                  label={<>{b.label} <Chip label={`${formatCurrency(getIncome(b.borrower))}/mo`} size="small" color="success" variant="outlined" sx={{ ml: 1 }} /></>}
                />
              ))}
            </Tabs>
          )}

          {/* Active Borrower Income Section */}
          {current && (
            <BorrowerIncomeSection
              borrower={current.borrower}
              borrowerIndex={current.index}
              namePath={`borrowers.${current.index}`}
              label={current.label}
              isEditMode={isEditMode}
            />
          )}
        </Grid>

        {/* Right column: Income summary sidebar (sticky) */}
        {showSidebar && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            {incomeSidebar}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default IncomeEmploymentTab;
